import React, { useContext, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import DocumentMeta from "react-document-meta";
import renderHtml from "react-render-html";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { FiBookOpen } from "react-icons/fi";
import HeaderBanner from "../Components/HeaderBanner";
import ContextComponent from "../Context";
import Loading from "./Loading";
import Product from "../Images/product.png";
import Statics from "../Statics";
import HelpSidebar from "./HelpSidebar";
import HelpSearchBox from "../Components/HelpSearchBox";
import {
  isHelpDesignPreview,
  sampleHelp,
  sampleHelpCategories,
} from "./Help.preview";
import styles from "./Help.module.css";

const bannerBody = (
  <>
    برای آموزش استفاده و کار با نرم افزار پژوهیار می‌توانید از محتواهای این بخش
    استفاده کنید.
    <br /> برای سایر بخش‌های پژوهیار راهنمای بیشتر و فیلم‌های آموزشی تهیه شده
    است.
  </>
);

export const formatHelpTitle = title =>
  title.replace(/\s+/g, "_").replace(/\u200C/g, "--");

const Help = () => {
  const context = useContext(ContextComponent);
  const { helpPost_id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const isPreview = isHelpDesignPreview(
    helpPost_id,
    location.search,
    process.env.NODE_ENV,
  );
  const basePath = location.pathname.startsWith("/help-my-lib/")
    ? "/help-my-lib"
    : "/help";
  const [allHelps, setAllHelps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingState, setLoadingState] = useState(Statics.NOT_LOADED);
  const [retryCount, setRetryCount] = useState(0);
  const [, setSearchedHelps] = useState([]);
  const { ApiDriver } = context;

  useEffect(() => {
    if (isPreview) {
      setAllHelps([sampleHelp]);
      setCategories(sampleHelpCategories);
      setLoadingState(Statics.LOADED);
      return;
    }
    let active = true;
    setAllHelps([]);
    setCategories([]);
    setLoadingState(Statics.LOADING);
    ApiDriver.getHelp(
      { page_size: 1000, _ts: Date.now() },
      data => {
        if (active) {
          setAllHelps(data.data || []);
          setLoadingState(Statics.LOADED);
        }
      },
      () => {
        if (active) setLoadingState(Statics.CAUSED_ERROR);
      },
    );
    ApiDriver.getHelpCategories(
      { parent_id: 0, scope: "general", _ts: Date.now() },
      data => {
        if (active) setCategories(data.data || []);
      },
      () => {},
    );
    return () => {
      active = false;
    };
  }, [ApiDriver, isPreview, retryCount]);

  const targetTitle = (helpPost_id || "")
    .replace(/_/g, " ")
    .replace(/--/g, "‌");
  const item = isPreview
    ? sampleHelp
    : allHelps.find(help =>
        /^\d+$/.test(helpPost_id || "")
          ? String(help.id) === helpPost_id
          : help.title === targetTitle,
      );
  useEffect(() => {
    if (item && !isPreview) {
      const canonicalPath = `${basePath}/${formatHelpTitle(item.title)}`;
      if (decodeURI(location.pathname) !== canonicalPath)
        history.replace(`${canonicalPath}${location.search}`);
    }
  }, [item, isPreview, basePath, location.pathname, location.search, history]);

  const content = useMemo(() => {
    const textOf = nodes =>
      React.Children.toArray(nodes)
        .map(node =>
          React.isValidElement(node)
            ? textOf(node.props.children)
            : String(node),
        )
        .join("");
    const decorate = nodes =>
      React.Children.map(nodes, node => {
        if (!React.isValidElement(node)) return node;
        const children = decorate(node.props.children);
        const entries = React.Children.toArray(node.props.children).filter(
          React.isValidElement,
        );
        const isShortList =
          node.type === "ul" &&
          entries.length > 1 &&
          entries.length <= 12 &&
          entries.every(
            entry =>
              entry.type === "li" &&
              React.Children.toArray(entry.props.children).every(
                child =>
                  !React.isValidElement(child) ||
                  ["strong", "b", "em", "a", "span"].includes(child.type),
              ) &&
              textOf(entry.props.children).length < 85,
          );
        const element = React.cloneElement(
          node,
          isShortList
            ? {
                className: `${node.props.className || ""} ${
                  styles.featureList
                }`,
              }
            : {},
          children,
        );
        return node.type === "table" ? (
          <div
            className={styles.tableScroll}
            role="region"
            aria-label="جدول راهنما"
            tabIndex={0}>
            {element}
          </div>
        ) : (
          element
        );
      });
    return decorate(renderHtml((item && item.body) || ""));
  }, [item]);

  return (
    <DocumentMeta
      title={`پژوهیار | ${item ? item.title : "راهنمای آموزش"}`}
      language="fa">
      <HeaderBanner
        body={bannerBody}
        title="راهنمای آموزش"
        image={Product}
        header_color="#cecbfa"
      />
      <Row
        className="help-section w-100 mx-0 p-5"
        style={{ background: "#eee" }}>
        <Col
          md={3}
          sm={12}
          style={{ paddingTop: "10px", paddingBottom: "10px" }}>
          <HelpSearchBox helps={allHelps} setSearchedHelps={setSearchedHelps} />
          <HelpSidebar sidebar_title="دسته بندی راهنما" data={categories} />
        </Col>
        <Col
          md={9}
          sm={12}
          style={{ paddingTop: "10px", paddingBottom: "10px" }}>
          {loadingState === Statics.NOT_LOADED && "بارگذاری نشده"}
          {loadingState === Statics.LOADING && (
            <div className="loading">
              <Loading />
            </div>
          )}
          {loadingState === Statics.CAUSED_ERROR && (
            <div className="error-parent" role="alert">
              <p>ارتباط با سرور با مشکل مواجه شد !</p>
              <button
                className={styles.retry}
                onClick={() => setRetryCount(count => count + 1)}
                type="button">
                تلاش دوباره
              </button>
            </div>
          )}
          {loadingState === Statics.LOADED && (
            <>
              <h3 className="post-category-title">
                راهنما &gt;{" "}
                {item && (
                  <>
                    {item.category ? `${item.category.title} > ` : ""}
                    {item.title}
                  </>
                )}
              </h3>
              {item ? (
                <article
                  className={styles.article}
                  aria-label={item.title}
                  dir="rtl">
                  {isPreview && (
                    <p className={styles.preview} role="note">
                      پیش‌نمایش طراحی · متن برگرفته از راهنمای فعلی سایت
                    </p>
                  )}
                  <header className={styles.readingHeader}>
                    <span className={styles.eyebrow}>
                      <FiBookOpen aria-hidden="true" /> راهنمای استفاده
                    </span>
                    <h1>{item.title}</h1>
                  </header>
                  <div className={styles.body}>{content}</div>
                </article>
              ) : (
                <div className="post-content">
                  برای نمایش راهنمای پژوهیار از منو عنوان موردنظر را انتخاب
                  کنید.
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
    </DocumentMeta>
  );
};

export default Help;
